import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CaretDown, Copy, FileText, FilmSlate, ShareNetwork, Sparkle } from "@phosphor-icons/react";
import api, { API } from "../api";
import { copyToClipboard } from "../lib/clipboard";
import { LIBRARY_SECTIONS } from "../data/libraryVideos";

const LIA_URL = "https://app.smartpeptides.ai";

// v414.5 — shareable customer documents. A collapsible card on My Store so
// distributors can copy/share the AXIS guides (injectables / nasal / cream)
// and the SmartPeptides LIA pitch without asking the owner for files.
// v414.84 — the Video Library joined the card: share the whole videos page
// (storefront link) or any single clip, same copy/share rows as the docs.
export const ShareDocsCard = ({ storeCode }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    api.get("/docs").then(({ data }) => setDocs(data)).catch(() => {});
  }, []);

  const docUrl = (slug) => `${API}/docs/${slug}`;
  const liaText = t("my_store.docs_lia_pitch", { url: LIA_URL });

  const share = async (title, text, url) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch {
        /* user closed the sheet — fall through to copy */
      }
    }
    const ok = await copyToClipboard(`${text} ${url}`);
    toast[ok ? "success" : "error"](ok ? t("my_store.docs_copied") : t("my_store.docs_copy_failed"));
  };

  const copyLink = async (payload) => {
    const ok = await copyToClipboard(payload);
    toast[ok ? "success" : "error"](ok ? t("my_store.docs_copied") : t("my_store.docs_copy_failed"));
  };

  if (docs.length === 0) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl" data-testid="my-store-docs-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left"
        data-testid="my-store-docs-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
          <FileText size={16} weight="bold" className="text-[#8a6a1c]" />
          {t("my_store.docs_title")}
        </span>
        <CaretDown size={14} weight="bold" className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2.5">
          <p className="text-xs text-zinc-500 -mt-1">{t("my_store.docs_hint")}</p>
          {docs.map((d) => (
            <div key={d.slug} className="flex items-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50/60 px-3 py-2.5" data-testid={`my-store-doc-${d.slug}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-zinc-900 truncate">{d.title}</p>
                <p className="text-[11px] text-zinc-500 truncate">{d.blurb}</p>
              </div>
              <button
                type="button"
                onClick={() => copyLink(docUrl(d.slug))}
                className="p-2 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                title={t("my_store.docs_copy")}
                data-testid={`my-store-doc-copy-${d.slug}`}
              >
                <Copy size={15} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => share(d.title, d.blurb, docUrl(d.slug))}
                className="p-2 rounded-lg text-white bg-[#8a6a1c] hover:opacity-90 transition-opacity"
                title={t("my_store.docs_share")}
                data-testid={`my-store-doc-share-${d.slug}`}
              >
                <ShareNetwork size={15} weight="bold" />
              </button>
            </div>
          ))}
          {/* v414.84 — Video Library: page link + every clip, shareable */}
          <p className="text-xs font-semibold text-zinc-900 pt-1.5 flex items-center gap-1.5">
            <FilmSlate size={13} weight="bold" className="text-[#8a6a1c]" />
            {t("my_store.docs_videos_header")}
          </p>
          {storeCode && (
            <div className="flex items-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50/60 px-3 py-2.5" data-testid="my-store-video-page-row">
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-zinc-900 truncate">{t("my_store.docs_video_page_title")}</p>
                <p className="text-[11px] text-zinc-500 truncate">{t("my_store.docs_video_page_sub")}</p>
              </div>
              <button
                type="button"
                onClick={() => copyLink(`${window.location.origin}/${encodeURIComponent(storeCode)}/videos`)}
                className="p-2 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                title={t("my_store.docs_copy")}
                data-testid="my-store-video-page-copy"
              >
                <Copy size={15} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => share(t("my_store.docs_video_page_title"), t("my_store.docs_video_page_sub"), `${window.location.origin}/${encodeURIComponent(storeCode)}/videos`)}
                className="p-2 rounded-lg text-white bg-[#8a6a1c] hover:opacity-90 transition-opacity"
                title={t("my_store.docs_share")}
                data-testid="my-store-video-page-share"
              >
                <ShareNetwork size={15} weight="bold" />
              </button>
            </div>
          )}
          {LIBRARY_SECTIONS.flatMap((s) => s.videos.map((v) => (
            <div key={v.id} className="flex items-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50/60 px-3 py-2.5" data-testid={`my-store-video-${v.id}`}>
              <span className="shrink-0 inline-flex items-center h-5 px-1.5 rounded-full text-[9px] font-bold uppercase bg-zinc-950 text-[#c9a24a]">{v.lang}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-zinc-900 truncate">{t(v.title)}</p>
                <p className="text-[11px] text-zinc-500 truncate">{t(`library.section_${s.key}`)}</p>
              </div>
              <button
                type="button"
                onClick={() => copyLink(v.url)}
                className="p-2 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                title={t("my_store.docs_copy")}
                data-testid={`my-store-video-copy-${v.id}`}
              >
                <Copy size={15} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => share(t(v.title), t("library.share_text", { name: t(v.title) }), v.url)}
                className="p-2 rounded-lg text-white bg-[#8a6a1c] hover:opacity-90 transition-opacity"
                title={t("my_store.docs_share")}
                data-testid={`my-store-video-share-${v.id}`}
              >
                <ShareNetwork size={15} weight="bold" />
              </button>
            </div>
          )))}
          <div className="flex items-center gap-2 rounded-lg border border-[#c9a84c66] bg-[#fbf6e7] px-3 py-2.5" data-testid="my-store-doc-lia">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-zinc-900 flex items-center gap-1.5">
                <Sparkle size={13} weight="fill" className="text-[#8a6a1c]" />
                {t("my_store.docs_lia_title")}
              </p>
              <p className="text-[11px] text-zinc-600 truncate">{t("my_store.docs_lia_sub")}</p>
            </div>
            <button
              type="button"
              onClick={() => copyLink(liaText)}
              className="p-2 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-white transition-colors"
              title={t("my_store.docs_copy")}
              data-testid="my-store-doc-copy-lia"
            >
              <Copy size={15} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => share("SmartPeptides — LIA", liaText, LIA_URL)}
              className="p-2 rounded-lg text-white bg-[#8a6a1c] hover:opacity-90 transition-opacity"
              title={t("my_store.docs_share")}
              data-testid="my-store-doc-share-lia"
            >
              <ShareNetwork size={15} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
